import * as yup from 'yup';
import {
  DocsRole,
  LedgerRole,
  Precedence,
  RoundAssignmentMode,
  RoundCreationSettingsDefaults,
  RoundCreationSettingsUpdate,
  RoundMatchMode,
  RoundSecurityType,
} from '../../../data-models/round-creation-settings.data-model';

export const WINDOW_MIN_DAYS = 1;
export const WINDOW_MAX_DAYS = 3650;

export type RoundCreationFormValues = RoundCreationSettingsDefaults;

export function windowIsRequired(mode: RoundMatchMode): boolean {
  return mode !== RoundMatchMode.STAGE;
}

function windowSchema(modeField: keyof RoundCreationFormValues) {
  return yup
    .number()
    .transform((value, original) => (original === '' || original === undefined ? null : value))
    .nullable()
    .integer('Whole days only')
    .when(modeField, {
      is: (mode: RoundMatchMode) => windowIsRequired(mode),
      then: (schema) =>
        schema
          .required('Enter a window in days')
          .min(WINDOW_MIN_DAYS, `At least ${WINDOW_MIN_DAYS} day`)
          .max(WINDOW_MAX_DAYS, `At most ${WINDOW_MAX_DAYS} days`),
      // The API rejects a window under STAGE; the form clears it rather than sending one.
      otherwise: (schema) => schema.transform(() => null),
    });
}

export function roundCreationConfigSchema() {
  return yup.object({
    roundCreationMatchMode: yup.mixed<RoundMatchMode>().oneOf(Object.values(RoundMatchMode)).required(),
    roundCreationWindowDays: windowSchema('roundCreationMatchMode'),
    transactionAssignmentMatchMode: yup
      .mixed<RoundMatchMode>()
      .oneOf(Object.values(RoundMatchMode))
      .required(),
    transactionAssignmentWindowDays: windowSchema('transactionAssignmentMatchMode'),
    unassignedGraceDays: yup
      .number()
      .transform((value, original) => (original === '' ? undefined : value))
      .typeError('Enter a number of days')
      .required('Enter a number of days')
      .integer('Whole days only')
      .min(0, 'Cannot be negative')
      .max(WINDOW_MAX_DAYS, `At most ${WINDOW_MAX_DAYS} days`),
    // Null is a real choice on these two: it hands the dial back to the client's legacy config.
    ledgerRole: yup
      .mixed<LedgerRole>()
      .nullable()
      .defined()
      .oneOf([...Object.values(LedgerRole), null]),
    // Nullable like ledgerRole: null means nothing has been chosen and the client's existing
    // roundProvider key still governs. Sending a value here is what makes the choice deliberate.
    docsRole: yup
      .mixed<DocsRole>()
      .nullable()
      .oneOf([...Object.values(DocsRole), null])
      .defined(),
    precedence: yup
      .mixed<Precedence>()
      .nullable()
      .defined()
      .oneOf([...Object.values(Precedence), null]),
    conversionsCreateRounds: yup.boolean().required(),
    assignmentMode: yup.mixed<RoundAssignmentMode>().oneOf(Object.values(RoundAssignmentMode)).required(),
    // Null means every kind may create. An empty list would mean none, which is never intended.
    securityTypes: yup
      .array()
      .of(yup.mixed<RoundSecurityType>().oneOf(Object.values(RoundSecurityType)).required())
      .min(1, 'Choose at least one security, or allow them all')
      .nullable()
      .defined(),
  });
}

/**
 * What the API accepts: a STAGE mode carries no window regardless of what the field last held.
 *
 * `resolvedDocsRole` is what an unchosen docsRole currently resolves to (the API's `effective`
 * block). The precedence control is shown against that resolved value, so the payload has to read
 * it the same way or a choice made on an inherited CREATE is dropped on the way out. Passing it
 * rather than writing `docsRole` keeps the dial itself unchosen: pinning it here would take the
 * client off their legacy configuration without anyone asking for that.
 */
export function toSettingsUpdate(
  values: RoundCreationFormValues,
  resolvedDocsRole?: DocsRole | null
): Required<RoundCreationSettingsUpdate> {
  const docsRole = values.docsRole ?? resolvedDocsRole ?? null;
  return {
    roundCreationMatchMode: values.roundCreationMatchMode,
    roundCreationWindowDays: windowIsRequired(values.roundCreationMatchMode)
      ? values.roundCreationWindowDays
      : null,
    transactionAssignmentMatchMode: values.transactionAssignmentMatchMode,
    transactionAssignmentWindowDays: windowIsRequired(values.transactionAssignmentMatchMode)
      ? values.transactionAssignmentWindowDays
      : null,
    unassignedGraceDays: values.unassignedGraceDays,
    ledgerRole: values.ledgerRole,
    docsRole: values.docsRole,
    // Precedence only decides anything when both sources may create; otherwise it is noise.
    precedence: docsRole === DocsRole.CREATE ? values.precedence : null,
    conversionsCreateRounds: values.conversionsCreateRounds,
    assignmentMode: values.assignmentMode,
    securityTypes: values.securityTypes,
  };
}
